<script lang="ts">
  import { submitFeedback } from '$lib/utils/api';
  import { isValidEmail } from '$lib/utils/helpers';
  import Loader from '$lib/components/Loader.svelte';

  const FEELING_OPTIONS = ['Delighted', 'Amused', 'Educated', 'Sad', 'Disgusted', 'Angered'];
  const HISTORY_OPTIONS = ['Yes', 'No', 'Maybe'];
  const PLAYING_OPTIONS = [
    'Only on this site',
    'I also get the level-up notifications to my email',
    'I am not really playing but I have an opinion'
  ];

  let feelings: string[] = [];
  let thoughts = '';
  let historyConnection = HISTORY_OPTIONS[HISTORY_OPTIONS.length - 1];
  let otherFeedback = '';
  let playingHow = PLAYING_OPTIONS[PLAYING_OPTIONS.length - 1];
  let contactInfo = '';
  let showEmailError = false;
  let showSubmitError = false;

  let isSubmitting = false;
  let showSuccess = false;

  async function handleSubmit() {
    showEmailError = contactInfo !== '' && !isValidEmail(contactInfo);
    if (showEmailError) return;

    isSubmitting = true;

    const result = await submitFeedback({
      feeling: feelings.join(', '),
      thoughts,
      historyConnection,
      otherFeedback,
      playingHow: [playingHow],
      email: contactInfo
    });

    isSubmitting = false;

    if (result.success) {
      showSuccess = true;
    } else {
      showSubmitError = true;
    }
  }
</script>

<svelte:head>
  <title>Feedback - outlive.it</title>
</svelte:head>

<section id="main" class="main">
  <header>
    <h2>Feedback</h2>
    <p class="subtitle">Let us know how the game made you feel</p>
  </header>

  {#if showSuccess}
    <div class="success-message">
      <i class="bi-check-circle-fill"></i> Thank you for your feedback!
    </div>
  {:else}
    <form on:submit|preventDefault={handleSubmit}>
      <fieldset>
        <legend>How do you feel about the game today?</legend>
        <div class="checkbox-group">
          {#each FEELING_OPTIONS as option}
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={feelings.includes(option)}
                on:change={() => feelings = feelings.includes(option) ? feelings.filter(f => f !== option) : [...feelings, option]}
                disabled={isSubmitting}
              />
              {option}
            </label>
          {/each}
        </div>
      </fieldset>

      <fieldset>
        <legend>Your feedback? For example tell a bit about the thoughts or feelings you have about Outlive or slow games in general</legend>
        <textarea
          bind:value={thoughts}
          rows="3"
          placeholder="Your answer"
          disabled={isSubmitting}
        ></textarea>
      </fieldset>

      <fieldset>
        <legend>Did it make you feel more connected with history?</legend>
        <div class="radio-group">
          {#each HISTORY_OPTIONS as option}
            <label class="radio-label">
              <input
                type="radio"
                name="historyConnection"
                value={option}
                bind:group={historyConnection}
                disabled={isSubmitting}
              />
              {option}
            </label>
          {/each}
        </div>
      </fieldset>

      <fieldset>
        <legend>How are you playing the game?</legend>
        <div class="radio-group">
          {#each PLAYING_OPTIONS as option}
            <label class="radio-label">
              <input
                type="radio"
                name="playingHow"
                value={option}
                bind:group={playingHow}
                disabled={isSubmitting}
              />
              {option}
            </label>
          {/each}
        </div>
      </fieldset>

      <fieldset>
        <legend>Your email <span class="optional">(optional, leave only if you want us to get in touch)</span></legend>
        <input
          type="text"
          bind:value={contactInfo}
          placeholder="Email"
          disabled={isSubmitting}
        />
      </fieldset>

      {#if showEmailError}
        <div class="error">
          <i class="bi-x-circle-fill"></i> Please check the email address.
        </div>
      {/if}

      {#if showSubmitError}
        <div class="error">
          <i class="bi-x-circle-fill"></i> Submission failed. Please try again.
        </div>
      {/if}

      <div class="submit-row">
        {#if isSubmitting}
          <Loader text="Sending..." />
        {:else}
          <button type="submit" class="button big">Submit</button>
        {/if}
      </div>
    </form>
  {/if}
</section>

<style>
  #main {
    padding: 4em 4em 2em 4em;
    background-color: #ffffff;
    border-radius: 4px;
  }

  #main > header:first-child {
    text-align: center;
    margin-bottom: 2em;
  }

  .subtitle {
    color: #666666;
    font-size: 1.1em;
    margin-top: -0.5em;
  }

  fieldset {
    border: none;
    padding: 0;
    margin: 0 0 2em 0;
  }

  legend {
    font-size: 1em;
    font-weight: 400;
    color: #444444;
    margin-bottom: 0.75em;
    padding: 0;
    width: 100%;
    border-bottom: 1px solid #eeeeee;
    padding-bottom: 0.4em;
  }

  .optional {
    font-size: 0.85em;
    color: #888888;
    font-weight: 400;
  }

  .radio-group,
  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
  }

  .radio-label,
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.6em;
    cursor: pointer;
    font-size: 1em;
    color: #444444;
    margin: 0;
  }

  .radio-label input,
  .checkbox-label input {
    width: auto;
    height: auto;
    display: inline;
    appearance: auto;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    accent-color: #ef6480;
  }

  textarea {
    width: 100%;
    resize: vertical;
    min-height: 5em;
  }

  .submit-row {
    margin-top: 1em;
  }

  .error {
    color: #ef6480;
    margin: 1em 0;
  }

  .success-message {
    color: #00aa00;
    font-size: 1.1em;
    padding: 2em 0;
    text-align: center;
  }

  @media screen and (max-width: 736px) {
    #main {
      padding: 2em 2em 1em 2em;
    }
  }

  @media screen and (max-width: 480px) {
    #main {
      padding: 2em 1em 1em 1em;
    }
  }
</style>
